import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { StorageProvider } from '../storage/storage';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { LoggerFactory } from '../logger/logger-factory.service';
import { environment } from '../../../../environments/environment';
import { FilterUser } from '../../../mnt/admin/configuration-management/configuration/configuration-creation/filer-user.model';
import { FindAll$Params } from '../../../api/fn/configuration-controller/find-all';
import { firstValueFrom } from 'rxjs';
import { ConfigurationDto } from '../../../api/models/configuration-dto';
import { ConfigurationControllerApi } from '../../../api/services/configuration-controller-api';

type DynamicToggleKey = keyof typeof DYNAMIC_TOGGLE;
type DynamicConfigKey = keyof typeof DYNAMIC_CONFIG;

/**
 * Configurations dynamiques provenant du serveur.
 * Ici la valeur est mise à undefined pour être sûr que la valeur renvoyée par getBooleanConfigOrElse
 * provienne : soit du serveur, soir de la valeur par défaut.
 *
 * Elle est mise à jour par ConfigService.syncConfig.
 */
export const DYNAMIC_TOGGLE = {
  TEST_CONFIG: undefined,
  TOGGLE_PAGE_OT_RELEVE_P1: undefined,
  TOGGLE_PAGE_OT_RELEVE_P1_USE_GPS: undefined,
  TOGGLE_DATADOG_LOGS: undefined,
  TOGGLE_CHATBOT_IA: undefined,
  TOGGLE_DEMANDE_PIECES_JOINTES: undefined,
  TOGGLE_CONSENTEMENT_GEOLOC: undefined,
  TOGGLE_PTI_BUTTON_SOS: undefined,
  TOGGLE_OPTIMISATION_RELEVE: undefined,
  TOGGLE_PASSWORD_EXPIRY: undefined,
  TOGGLE_TOP_INTERVENTION: undefined,
  TOGGLE_ADRESSE_INTERVENTION: undefined,
  TOGGLE_PORTEFEUILLE_PERIMETRE_ISOLE: undefined,
  GESTION_STOCKS: undefined,
};
export const DYNAMIC_CONFIG = {
  VERSION_APP_MINIMALE_REQUISE: undefined,
  CHATBOT_AI_TYPE: undefined,
  KPI_OBJECTIF_FIL_EAU: undefined,
  KPI_OBJECTIF_REALISATION: undefined,
  KPI_OBJECTIF_AGENDA: undefined,
  ADFS_PASSWORD_DURATION: undefined,
  DAYS_UNTIL_PASSWORD_EXPIRY_ALERT: undefined,
  BACKGROUND_GEOLOCATION_DISTANCE_FILTER_METRES: undefined,
  BACKGROUND_GEOLOCATION_INTERVAL_MINUTES: undefined,
  GEOFENCE_RADIUS_METRES: undefined,
  PRIVACY_POLICY_URL: undefined,
  GEOLOCATION_CHARTER_URL: undefined,
};

export type OnOff = 'ON' | 'OFF';

/**
 * Retourne la valeur liée à la la clé de la toggle feature dynamique donnée.
 *
 * @param key la clé de la toggle feature dynamique.
 * @param defaultValue une valeur par défaut.
 */
export function getOnOffConfigOrElse(key: DynamicToggleKey, defaultValue: OnOff): OnOff {
  if (DYNAMIC_TOGGLE[key] === null || DYNAMIC_TOGGLE[key] === undefined) {
    return defaultValue;
  }
  return DYNAMIC_TOGGLE[key] as OnOff;
}
export function getConfigOrElse(key: DynamicConfigKey, defaultValue: string): string {
  if (DYNAMIC_CONFIG[key] === null || DYNAMIC_CONFIG[key] === undefined) {
    return defaultValue;
  }
  return DYNAMIC_CONFIG[key] as string;
}

export function overrideConfiguration(serverConf: ConfigurationDto[]) {
  serverConf.forEach((value) => {
    if (value.toggleFeature) {
      DYNAMIC_TOGGLE[value.cle] = value.valeur;
    } else {
      DYNAMIC_CONFIG[value.cle] = value.valeur;
    }
  });
  console.log('DYNAMIC_CONFIG', DYNAMIC_CONFIG);
  console.log('DYNAMIC_TOGGLE', DYNAMIC_TOGGLE);
}

export type ConfigParUtilisateur = {
  id: string;
  nom: string;
  prenom: string;
  dkCodeRessource: string;
  valeur: OnOff;
};

export type ConfigData = {
  cle: string;
  valeur: OnOff;
  perUtilisateur: ConfigParUtilisateur[];
};

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private userId: string;

  adminToggleFeatureUrl: string;

  private logger: LoggerWrapper;

  constructor(
    private configApi: ConfigurationControllerApi,
    private storageProvider: StorageProvider,
    private http: HttpClient,
    private loggerFactory: LoggerFactory
  ) {
    this.adminToggleFeatureUrl = environment.apis.API_DACTECH_BACK_ROOT + '/v1/admin/configuration/toggle-feature';
    this.logger = this.loggerFactory.getInstance(this);
  }

  async syncConfig(userId: string) {
    this.userId = userId;
    const localConfig: ConfigurationDto[] = await this.storageProvider.get(`${this.userId}_config`, false);
    if (localConfig) {
      overrideConfiguration(localConfig);
    }

    const remoteConfig: ConfigurationDto[] = await this.getRemoteConfig(this.userId);
    if (remoteConfig) {
      overrideConfiguration(remoteConfig);
      console.log('set storage with', `${this.userId}_config`, remoteConfig);
      await this.storageProvider.set(`${this.userId}_config`, remoteConfig, false);
    }
  }

  private async getRemoteConfig(userId: string): Promise<ConfigurationDto[]> {
    try {
      let params: FindAll$Params = {
        idUser: userId,
      };
      return await firstValueFrom(this.configApi.findAll(params));
    } catch (err) {
      return undefined;
    }
  }

  async getConfigCles(): Promise<Array<string>> {
    try {
      return await this.http.get<string[]>(this.adminToggleFeatureUrl + `/cles`).toPromise();
    } catch (err) {
      return undefined;
    }
  }

  async getFullConfig(cle: string): Promise<ConfigData> {
    try {
      return await this.http.get<ConfigData>(this.adminToggleFeatureUrl + `/${cle}/full`).toPromise();
    } catch (err) {
      return undefined;
    }
  }

  async patchConfig(cle, config) {
    await this.http
      .patch(this.adminToggleFeatureUrl + `/cles/${cle}/update`, config, { observe: 'response' })
      .toPromise();
  }

  async patchConfigPerUser(cle, configPerUser) {
    await this.http
      .patch(this.adminToggleFeatureUrl + `/cles/${cle}/users/update`, configPerUser, { observe: 'response' })
      .toPromise();
  }

  async postConfigPerUser(cle: string, configPerUser) {
    await this.http
      .post(this.adminToggleFeatureUrl + `/cles/${cle}/users/create`, configPerUser, {
        observe: 'response',
        responseType: 'text',
      })
      .toPromise();
  }

  async deleteConfigPerUser(cle: string, userId: string) {
    await this.http
      .delete(this.adminToggleFeatureUrl + `/cles/${cle}/users/${userId}/delete`, {
        observe: 'response',
        responseType: 'text',
      })
      .toPromise();
  }

  async filterUsers(filterText: string) {
    try {
      return await this.http.get<FilterUser[]>(this.adminToggleFeatureUrl + `/users/filter/${filterText}`).toPromise();
    } catch (err) {
      return undefined;
    }
  }
}
